import type { DeviceSummary } from "@fistos/shared/api";
import { A } from "@solidjs/router";
import { Bot, ChevronRight, Smartphone } from "lucide-solid";
import { createMemo, For, Show } from "solid-js";
import { BatteryBadge, OnlineBadge, ToneBadge } from "@/components/badges";
import { EnrollDialog } from "@/components/EnrollDialog";
import { Card } from "@/components/ui/card";
import { now } from "@/lib/clock";
import { ago } from "@/lib/format";
import { live } from "@/lib/live";

function DeviceCard(props: { d: DeviceSummary }) {
  return (
    <A
      href={`/devices/${props.d.id}`}
      class="group focus-visible:ring-ring/50 rounded-xl outline-none focus-visible:ring-[3px]"
    >
      <Card class="group-hover:border-foreground/20 h-full gap-4 p-5 transition-colors">
        <div class="flex items-start gap-3">
          <div class="bg-muted flex size-10 shrink-0 items-center justify-center rounded-lg">
            <Smartphone class="size-5" />
          </div>
          <div class="min-w-0 flex-1">
            <div class="truncate font-medium">{props.d.name}</div>
            <div class="text-muted-foreground truncate text-sm">
              {props.d.model}
              <Show when={props.d.fistosVersion}> · FistOS {props.d.fistosVersion}</Show>
            </div>
          </div>
          <ChevronRight class="text-muted-foreground size-4 opacity-0 transition-opacity group-hover:opacity-100" />
        </div>
        <div class="flex flex-wrap items-center gap-1.5">
          <OnlineBadge online={props.d.online} />
          <Show when={props.d.online && props.d.battery}>
            {(b) => <BatteryBadge percent={b().percent} charging={b().charging} />}
          </Show>
          <Show when={props.d.activeTaskId}>
            <ToneBadge tone="busy">
              <Bot /> Working
            </ToneBadge>
          </Show>
        </div>
        <div class="text-muted-foreground text-xs">
          <Show when={props.d.online} fallback={<>Last seen {ago(props.d.lastSeenAt, now())}</>}>
            Connected
          </Show>
        </div>
      </Card>
    </A>
  );
}

export function DevicesPage() {
  const devices = createMemo(() =>
    Object.values(live.devices).sort((a, b) => Number(b.online) - Number(a.online) || a.name.localeCompare(b.name)),
  );
  return (
    <div class="space-y-6">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 class="text-2xl font-semibold tracking-tight">Devices</h1>
          <p class="text-muted-foreground text-sm">Phones enrolled with this server.</p>
        </div>
        <EnrollDialog />
      </div>
      <Show
        when={devices().length}
        fallback={
          <Card class="items-center gap-3 border-dashed p-12 text-center">
            <div class="bg-muted flex size-12 items-center justify-center rounded-full">
              <Smartphone class="size-6" />
            </div>
            <div class="font-medium">No devices yet</div>
            <p class="text-muted-foreground max-w-sm text-sm">
              Enroll a phone running FistOS: it connects to this server over a pinned, mutually authenticated link.
            </p>
            <EnrollDialog />
          </Card>
        }
      >
        <div class="grid gap-4 *:min-w-0 sm:grid-cols-2 lg:grid-cols-3">
          <For each={devices()}>{(d) => <DeviceCard d={d} />}</For>
        </div>
      </Show>
    </div>
  );
}
